import React from "react";
import {
  Briefcase,
  Users,
  Code2,
  Rocket,
  Trophy,
  TrendingUp,
  Layers,
  CircleDot,
} from "lucide-react";
import {
  AchievementsIntelligence,
  CareerIntelligence,
  ImpactIntelligence,
  LeadershipIntelligence,
  SkillsIntelligence,
} from "../Types/CandidateIntelligenceTypes";
import { Section, EmptyState } from "./Section";

const Pill: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <span className="rounded-full border border-slate-700 bg-slate-800/70 px-2 py-0.5 text-[11px] text-slate-300">
    {children}
  </span>
);

export const CareerIntelligenceCard: React.FC<{ data: CareerIntelligence }> = ({ data }) => (
  <Section icon={<Briefcase size={17} />} title="Career Intelligence" subtitle="Where you are (and trajectory)">
    <div className="space-y-2 text-sm">
      <p className="text-xs font-bold uppercase tracking-wide text-slate-500">Current</p>
      <p className="text-slate-100">
        {data.currentRole ?? "—"} <span className="text-slate-500">@</span>{" "}
        <span className="font-semibold text-cyan-300">{data.currentCompany ?? "—"}</span>
      </p>
      <div className="grid grid-cols-2 gap-2 pt-1 text-xs">
        <div className="rounded-xl border border-slate-800 bg-slate-900/60 p-2.5">
          <p className="text-slate-500">Experience</p>
          <p className="font-black text-slate-100">{data.totalYearsOfExperience ?? 0} yrs</p>
        </div>
        <div className="rounded-xl border border-slate-800 bg-slate-900/60 p-2.5">
          <p className="text-slate-500">Stage</p>
          <p className="font-black text-slate-100">{data.progressionStage ?? "—"}</p>
        </div>
        <div className="rounded-xl border border-slate-800 bg-slate-900/60 p-2.5">
          <p className="text-slate-500">Leadership yrs</p>
          <p className="font-black text-slate-100">{data.leadershipYears ?? 0}</p>
        </div>
        <div className="rounded-xl border border-slate-800 bg-slate-900/60 p-2.5">
          <p className="text-slate-500">Team size</p>
          <p className="font-black text-slate-100">{data.teamSizeLed ? `${data.teamSizeLed}+` : "—"}</p>
        </div>
      </div>
      {data.notableCompanies?.length ? (
        <div className="flex flex-wrap gap-1.5 pt-1">
          {data.notableCompanies.map((c) => (
            <Pill key={c}>{c}</Pill>
          ))}
        </div>
      ) : null}
    </div>
  </Section>
);

export const LeadershipIntelligenceCard: React.FC<{ data: LeadershipIntelligence }> = ({ data }) => (
  <Section
    icon={<Users size={17} />}
    title="Leadership Intelligence"
    subtitle={data.managementLevelBucket ?? "Scope not yet defined"}
  >
    <div className="space-y-2">
      <div className="flex items-center gap-3">
        <span
          className={`grid h-10 w-10 place-items-center rounded-xl text-lg font-black ${
            data.leadershipSignal ? "bg-emerald-500/15 text-emerald-300" : "bg-slate-800 text-slate-500"
          }`}
        >
          {data.leadershipScore.toFixed(0)}
        </span>
        <div className="text-sm">
          <p className={`font-bold ${data.leadershipSignal ? "text-emerald-300" : "text-slate-500"}`}>
            {data.leadershipSignal ? "Leadership signal detected" : "Leadership scope not yet filled"}
          </p>
          <p className="text-xs text-slate-500">Leadership score · team {data.teamSizeLed ? `${data.teamSizeLed}+` : "—"}</p>
        </div>
      </div>
      <div className="flex flex-wrap gap-1.5">
        {data.ownerShipLevels?.length ? (
          data.ownerShipLevels.map((lvl) => (
            <span key={lvl} className="flex items-center gap-1 rounded-full border border-indigo-500/30 bg-indigo-500/10 px-2 py-0.5 text-[11px] text-indigo-200">
              <Layers size={11} /> {lvl}
            </span>
          ))
        ) : (
          <span className="text-xs text-slate-600">No ownership levels listed</span>
        )}
      </div>
      {data.hiringAuthority?.length ? (
        <p className="text-xs text-slate-400">
          Hiring authority: <span className="text-slate-200">{data.hiringAuthority.join(", ")}</span>
        </p>
      ) : null}
      {data.responsibilities && data.responsibilities.length > 0 ? (
        <ul className="flex flex-col gap-1 pt-1">
          {data.responsibilities.slice(0, 3).map((r, i) => (
            <li key={i} className="flex items-start gap-1.5 text-xs text-slate-300">
              <CircleDot size={11} className="mt-0.5 shrink-0 text-indigo-400" />
              {r}
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  </Section>
);

export const SkillsIntelligenceCard: React.FC<{ data: SkillsIntelligence }> = ({ data }) => (
  <Section
    icon={<Code2 size={17} />}
    title="Skills Intelligence"
    subtitle={`${data.totalSkills} skills across ${data.categories.length} categories`}
  >
    {data.skills.length === 0 ? (
      <EmptyState label="Add skills to unlock this insight" />
    ) : (
      <div className="flex flex-wrap gap-1.5">
        {data.skills.slice(0, 16).map((s) => (
          <span
            key={`${s.skill}`}
            title={`${s.category}${s.years ? ` · ~${s.years} yrs` : ""}`}
            className="rounded-full border border-slate-700 bg-slate-900/70 px-2.5 py-1 text-[11px] font-semibold text-slate-200"
          >
            {s.skill}
            {s.category === "Core expertise" ? <span className="ml-1 text-cyan-400">✦</span> : null}
          </span>
        ))}
      </div>
    )}
  </Section>
);

export const ImpactIntelligenceCard: React.FC<{ data: ImpactIntelligence }> = ({ data }) => (
  <Section
    icon={<Rocket size={17} />}
    title="Business Impact Intelligence"
    subtitle={data.scaleBucket ?? "Impact scale"}
  >
    <div className="space-y-2">
      <div className="flex flex-wrap gap-1.5">
        {data.impactTypes?.length ? (
          data.impactTypes.map((t) => <Pill key={t}>{t}</Pill>)
        ) : (
          <span className="text-xs text-slate-600">No impact types listed</span>
        )}
      </div>
      {data.impactDescriptions?.length ? (
        <ul className="flex flex-col gap-1.5">
          {data.impactDescriptions.slice(0, 3).map((desc, i) => (
            <li key={i} className="rounded-lg border border-emerald-500/20 bg-emerald-500/5 px-2.5 py-1.5 text-xs text-slate-300">
              <TrendingUp size={11} className="mr-1 inline text-emerald-400" />
              {desc}
            </li>
          ))}
        </ul>
      ) : (
        <p className="text-xs text-slate-600">Add quantified impact examples to strengthen this.</p>
      )}
    </div>
  </Section>
);

export const AchievementsIntelligenceCard: React.FC<{ data: AchievementsIntelligence }> = ({ data }) => (
  <Section
    icon={<Trophy size={17} />}
    title="Achievements Intelligence"
    subtitle={`${data.achievements?.length ?? 0} achievements recorded`}
  >
    {!data.achievements?.length && !data.reputationSignals?.length ? (
      <EmptyState label="Add achievements and reputation signals" />
    ) : (
      <div className="space-y-2">
        {data.achievements?.length ? (
          <ul className="flex flex-col gap-1.5">
            {data.achievements.slice(0, 4).map((a, i) => (
              <li key={i} className="flex items-start gap-1.5 text-xs text-slate-300">
                <Trophy size={11} className="mt-0.5 shrink-0 text-amber-400" />
                {a}
              </li>
            ))}
          </ul>
        ) : null}
        {data.reputationSignals?.length ? (
          <div className="flex flex-wrap gap-1.5 border-t border-slate-800 pt-2">
            {data.reputationSignals.map((s) => (
              <Pill key={s}>{s}</Pill>
            ))}
          </div>
        ) : null}
      </div>
    )}
  </Section>
);